import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Gauge, Package, Truck, Factory, Kanban, Users, ChartBar, ClipboardText,
  Storefront, Handshake, IdentificationCard, Buildings, Coins, ChartLine,
  Toolbox, Repeat, CalendarBlank, Barcode, ChatCircleDots, Gear, CaretDown,
  MagnifyingGlass, Sun, MoonStars, ArrowSquareOut, X, Kanban as KanbanIco,
  Bank,
} from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { api } from "../lib/api";
import RequestsBell from "./RequestsBell";
import NotificationsBell from "./NotificationsBell";
import PoolQueuePill from "./PoolQueuePill";
import UserProfileChip from "./UserProfileChip";

const GROUPS = [
  {
    title: "Overview", items: [
      { to: "/dashboard", screen: "dashboard", label: "Dashboard", icon: Gauge, desc: "KPIs & today's snapshot" },
    ]
  },
  {
    title: "Sales", items: [
      { to: "/customers", screen: "customers", label: "Customers", icon: Storefront, desc: "Customer master + outstanding" },
      { to: "/quotations", screen: "quotations", label: "Quotations", icon: ClipboardText, desc: "Offer / estimate documents" },
      { to: "/sales-invoices", screen: "sales_invoices", label: "Sales Invoices", icon: ClipboardText, desc: "GST-compliant invoices" },
      { to: "/meetings", screen: "meetings", label: "Meetings & Calls", icon: CalendarBlank, desc: "Log conversations" },
      { to: "/enquiries", screen: "users", label: "Enquiries", icon: ChatCircleDots, desc: "Landing page leads" },
    ]
  },
  {
    title: "Operations", items: [
      { to: "/inventory", screen: "inventory", label: "Inventory", icon: Package, desc: "Raw materials & barcodes" },
      { to: "/raw-materials", screen: "inventory", label: "Raw Materials", icon: Package, desc: "Master list of materials you buy" },
      { to: "/production", screen: "production", label: "Production", icon: Factory, desc: "Daily & monthly targets" },
      { to: "/batches", screen: "batches", label: "Batches", icon: Truck, desc: "Batch traceability" },
      { to: "/attendance", screen: "attendance", label: "Attendance", icon: Users, desc: "Workers & leave" },
      { to: "/products", screen: "products", label: "Products", icon: Barcode, desc: "SKUs & pricing" },
      { to: "/barcodes", screen: "barcodes", label: "Barcodes", icon: Barcode, desc: "Barcode scanner" },
      { to: "/issues", screen: "issues", label: "Issues", icon: Kanban, desc: "Task board" },
    ]
  },
  {
    title: "Purchases", items: [
      { to: "/suppliers", screen: "suppliers", label: "Suppliers", icon: Handshake, desc: "Vendor master" },
      { to: "/purchase-orders", screen: "purchase_orders", label: "Purchase Orders", icon: ClipboardText, desc: "Raise POs to suppliers" },
      { to: "/misc-purchases", screen: "misc_purchases", label: "Expenses", icon: Toolbox, desc: "Purchases & reimbursements" },
    ]
  },
  {
    title: "Finance", items: [
      { to: "/accounting", screen: "accounting", label: "Accounting", icon: Coins, desc: "Ledger & bank" },
      { to: "/loans", screen: "accounting", label: "Loans", icon: Bank, desc: "Business loans · EMI · repayment" },
      { to: "/pnl", screen: "pnl", label: "Monthly P&L", icon: ChartLine, desc: "Profit / loss" },
      { to: "/subscriptions", screen: "subscriptions", label: "Subscriptions", icon: Repeat, desc: "Recurring costs" },
      { to: "/company-expenses", screen: "users", label: "Expense Sheet", icon: Coins, desc: "Complete company outgoings (admin)" },
      { to: "/gst-reports", screen: "users", label: "GST Reports", icon: ClipboardText, desc: "Monthly filing package for CA · Excel export" },
    ]
  },
  {
    title: "People", items: [
      { to: "/employees", screen: "employees", label: "Employees", icon: IdentificationCard, desc: "Payroll master" },
    ]
  },
  {
    title: "Admin", items: [
      { to: "/users", screen: "users", label: "Users", icon: Users, desc: "Roles & access" },
      { to: "/requests", screen: "requests", label: "Requests", icon: ClipboardText, desc: "Approval queue" },
      { to: "/audit-log", screen: "users", label: "Audit Log", icon: ClipboardText, desc: "Who did what, and when" },
      { to: "/settings", screen: "settings", label: "Settings", icon: Gear, desc: "Business config" },
    ]
  },
];

function useAllowedGroups() {
  const { user } = useAuth();
  return useMemo(() => {
    if (!user) return [];
    return GROUPS
      .map(g => ({
        ...g,
        items: g.items.filter(i => user.role === "admin" || (user.permissions || []).includes(i.screen)),
      }))
      .filter(g => g.items.length > 0);
  }, [user]);
}

export default function TopNav() {
  const [open, setOpen] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const { user } = useAuth();
  const { mode, toggle } = useTheme();
  const allowedGroups = useAllowedGroups();

  const dropRef = useRef(null);
  useEffect(() => {
    const onDown = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);
  useEffect(() => { setOpen(null); }, [loc.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "Escape") { setSearchOpen(false); setOpen(null); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <header className="sticky top-0 z-40 bg-zinc-900/95 text-white border-b border-zinc-800 backdrop-blur-md shadow-[0_2px_20px_-5px_rgba(0,0,0,0.3)]">
        <div className="max-w-[1800px] mx-auto px-4 lg:px-8 h-16 flex items-center gap-4 lg:gap-6">
          
          {/* Brand Logo - Clean View */}
          <Link to="/dashboard" className="flex items-center shrink-0 transition-transform duration-200 active:scale-95">
            <img src="/logo/eaths-logo.webp" alt="Eath's" className="h-9 w-auto object-contain" />
          </Link>

          {/* Desktop Navigation */}
          <nav ref={dropRef} className="hidden lg:flex items-center gap-1.5 flex-1 h-full" data-testid="top-nav">
            {allowedGroups.map((g, i) => {
              const allowed = g.items;
              const isOpen = open === i;
              const hasActive = allowed.some(a => loc.pathname === a.to);
              return (
                <div key={g.title} className="relative flex items-center h-full">
                  <button
                    onClick={() => setOpen(isOpen ? null : i)}
                    onMouseEnter={() => open !== null && setOpen(i)}
                    data-testid={`nav-group-${g.title.toLowerCase()}`}
                    className={`px-3.5 py-2 text-[11px] font-semibold tracking-wider uppercase flex items-center gap-1.5 rounded-lg transition-all duration-200 group ${
                      hasActive 
                        ? "bg-white/10 text-white shadow-sm ring-1 ring-white/10" 
                        : "text-zinc-400 hover:text-[color:var(--kr-orange)] hover:bg-white/5"
                    }`}
                  >
                    <span>{g.title}</span>
                    <CaretDown size={11} weight="bold" className={`transition-transform duration-200 ease-out ${isOpen ? "rotate-180 text-white" : "text-zinc-500 group-hover:text-[color:var(--kr-orange)]"}`} />
                  </button>

                  {/* Dropdown Container */}
                  {isOpen && (
                    <div 
                      className="absolute left-0 top-[calc(100%-8px)] w-80 bg-white dark:bg-neutral-900 text-zinc-900 dark:text-zinc-100 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.3)] rounded-xl border border-zinc-200 dark:border-neutral-800 p-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200" 
                      data-testid={`nav-dropdown-${g.title.toLowerCase()}`}
                    >
                      <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-400 dark:text-neutral-500 border-b border-zinc-100 dark:border-neutral-800/60 mb-1">
                        {g.title} Management
                      </div>
                      <div className="grid grid-cols-1 gap-0.5">
                        {allowed.map(item => {
                          const Icon = item.icon;
                          const active = loc.pathname === item.to;
                          return (
                            <NavLink
                              key={item.to}
                              to={item.to}
                              className={`flex items-start gap-3.5 px-3 py-2.5 rounded-lg transition-all group ${
                                active 
                                  ? "bg-[color:var(--kr-orange)]/5 text-[color:var(--kr-orange)] font-medium shadow-sm ring-1 ring-[color:var(--kr-orange)]/10" 
                                  : "hover:bg-zinc-50 dark:hover:bg-neutral-800 text-zinc-700 dark:text-zinc-300 hover:text-[color:var(--kr-orange)] dark:hover:text-[color:var(--kr-orange)]"
                              }`}
                            >
                              <div className={`p-1.5 rounded-md shrink-0 transition-colors ${
                                active ? "bg-[color:var(--kr-orange)]/10 text-[color:var(--kr-orange)]" : "bg-zinc-100 dark:bg-neutral-800 text-zinc-500 group-hover:text-[color:var(--kr-orange)]"
                              }`}>
                                <Icon size={16} weight={active ? "bold" : "regular"} />
                              </div>
                              <div className="min-w-0 pt-0.5">
                                <div className="text-sm font-semibold tracking-tight leading-none mb-1">{item.label}</div>
                                {item.desc && <div className="text-xs text-zinc-400 dark:text-neutral-500 line-clamp-1 group-hover:text-[color:var(--kr-orange)]/80 dark:group-hover:text-[color:var(--kr-orange)]/80">{item.desc}</div>}
                              </div>
                            </NavLink>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Responsive Search Trigger */}
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="topnav-search-btn"
            className="hidden md:flex items-center justify-center lg:justify-start gap-2.5 w-10 h-10 lg:w-auto lg:px-3 lg:py-2 bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white text-xs rounded-lg border border-white/10 shadow-inner transition-all duration-150 group shrink-0"
          >
            <MagnifyingGlass size={16} weight="bold" className="group-hover:scale-110 transition-transform shrink-0" />
            <span className="hidden lg:inline font-medium tracking-wide">Search system...</span>
            <kbd className="hidden lg:inline ml-6 text-[10px] px-2 py-0.5 bg-white/10 text-zinc-300 rounded font-mono shadow-sm">⌘K</kbd>
          </button>

          {/* Mobile hamburger menu */}
          <MobileMenu />

          {/* Right Actions */}
          <div className="flex items-center gap-2 md:gap-2.5 ml-auto shrink-0">
            <button
              onClick={toggle}
              data-testid="theme-toggle"
              title={`Switch to ${mode === "dark" ? "light" : "dark"} mode`}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white ring-1 ring-white/5 transition-all duration-200 active:scale-90"
            >
              {mode === "dark" ? <Sun size={18} weight="bold" /> : <MoonStars size={18} weight="bold" />}
            </button>
            
            <div className="hidden md:block transition-transform hover:scale-105"><RequestsBell /></div>
            <div className="transition-transform hover:scale-105"><PoolQueuePill /></div>
            <div className="transition-transform hover:scale-105"><NotificationsBell /></div>
            
            {/* User Profile Container */}
            <div className="pl-1 border-l border-white/10 ml-1 [&_.user-details-text]:hidden lg:[&_.user-details-text]:block">
              <UserProfileChip />
            </div>
          </div>
        </div>
      </header>

      {searchOpen && <SearchPalette onClose={() => setSearchOpen(false)} />}
    </>
  );
}

// ---------- Search palette ----------
function SearchPalette({ onClose }) {
  const { user } = useAuth();
  const can = (screen) => user?.role === "admin" || (user?.permissions || []).includes(screen);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ customers: [], suppliers: [], products: [], issues: [], docs: [] });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (!q.trim()) { setResults({ customers: [], suppliers: [], products: [], issues: [], docs: [] }); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const noop = { data: [] };
        const [pC, pS, pr, iss, docs] = await Promise.all([
          can("customers") ? api.get("/parties", { params: { kind: "customer" } }) : Promise.resolve(noop),
          can("suppliers") ? api.get("/parties", { params: { kind: "supplier" } }) : Promise.resolve(noop),
          can("products") ? api.get("/products") : Promise.resolve(noop),
          can("issues") ? api.get("/issues") : Promise.resolve(noop),
          (can("sales_invoices") || can("quotations") || can("purchase_orders"))
            ? api.get("/documents") : Promise.resolve(noop),
        ]);
        if (cancelled) return;
        const needle = q.toLowerCase();
        const match = (s) => (s || "").toLowerCase().includes(needle);
        setResults({
          customers: pC.data.filter(x => match(x.name) || match(x.gstin)).slice(0, 5),
          suppliers: pS.data.filter(x => match(x.name) || match(x.gstin)).slice(0, 5),
          products: pr.data.filter(x => match(x.name) || match(x.sku_code)).slice(0, 5),
          issues: iss.data.filter(x => match(x.title) || match(x.issue_no)).slice(0, 5),
          docs: docs.data.filter(x => match(x.doc_no) || match(x.party?.name)).slice(0, 5),
        });
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.debug("Search palette fetch failed:", e?.message);
      }
      finally { if (!cancelled) setLoading(false); }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const go = (path) => { onClose(); nav(path); };
  const total = Object.values(results).reduce((s, arr) => s + arr.length, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 px-4 bg-zinc-950/60 backdrop-blur-sm transition-opacity duration-200" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-white dark:bg-neutral-900 shadow-[0_25px_70px_-10px_rgba(0,0,0,0.4)] rounded-2xl border border-zinc-200 dark:border-neutral-800 overflow-hidden transform transition-all scale-100"
        data-testid="search-palette"
      >
        <div className="flex items-center gap-3.5 px-4.5 py-4 border-b border-zinc-150 dark:border-neutral-800 bg-zinc-50 dark:bg-neutral-900/50">
          <MagnifyingGlass size={22} weight="bold" className="text-zinc-400 dark:text-neutral-500" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search dashboard metrics, customers, suppliers, SKUs..."
            className="flex-1 bg-transparent text-zinc-900 dark:text-white text-base font-normal outline-none placeholder-zinc-400 dark:placeholder-neutral-500"
            data-testid="search-input"
          />
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-zinc-200/60 dark:hover:bg-neutral-800 text-zinc-400 hover:text-zinc-700 dark:hover:text-white transition-colors">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="max-h-[55vh] overflow-y-auto p-2.5 custom-scrollbar">
          {!q.trim() && (
            <div className="p-8 text-center text-zinc-400 dark:text-neutral-500">
              <div className="text-sm font-medium mb-1.5">Omni Search Terminal</div>
              <div className="text-xs font-mono opacity-80">Use ↑ ↓ elements · ↵ to trigger action · esc to abort panel</div>
            </div>
          )}
          {q.trim() && total === 0 && !loading && (
            <div className="p-8 text-center text-sm text-zinc-400 dark:text-neutral-500">No indexing match records for “<span className="text-zinc-700 dark:text-zinc-300 font-medium">{q}</span>”</div>
          )}
          {loading && q.trim() && (
            <div className="p-4 text-xs text-[color:var(--kr-orange)] font-mono flex items-center gap-2">
              <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
              <span>Querying cloud datastore database context entries...</span>
            </div>
          )}
          <SearchGroup title="Customers" items={results.customers} render={c => c.name} onGo={() => go("/customers")} />
          <SearchGroup title="Suppliers" items={results.suppliers} render={c => c.name} onGo={() => go("/suppliers")} />
          <SearchGroup title="Products" items={results.products} render={c => `${c.name}${c.sku_code ? " · " + c.sku_code : ""}`} onGo={() => go("/products")} />
          <SearchGroup title="Issues" items={results.issues} render={c => `${c.issue_no} — ${c.title}`} onGo={() => go("/issues")} />
          <SearchGroup title="Documents" items={results.docs} render={c => `${c.doc_no} — ${c.party?.name}`} onGo={() => go("/documents")} />
        </div>
      </div>
    </div>
  );
}

function SearchGroup({ title, items, render, onGo }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-3 last:mb-1">
      <div className="px-3 py-1 text-[10px] font-bold font-mono uppercase tracking-widest text-zinc-400 dark:text-neutral-500">{title}</div>
      <div className="space-y-0.5 mt-1">
        {items.map(x => (
          <button
            key={x.id}
            onClick={onGo}
            className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-zinc-50 dark:hover:bg-neutral-800/60 text-sm text-zinc-700 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-white flex items-center justify-between group transition-colors"
          >
            <span className="font-medium truncate pr-4">{render(x)}</span>
            <ArrowSquareOut size={14} className="text-zinc-400 opacity-0 group-hover:opacity-100 transition-all transform translate-x-1 group-hover:translate-x-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------- Mobile navigation overlay ----------
function MobileMenu() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const allowedGroups = useAllowedGroups();
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  return (
    <div className="lg:hidden">
      <button 
        onClick={() => setOpen(o => !o)} 
        className={`p-2 rounded-xl transition-colors ${open ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}
      >
        <KanbanIco size={20} weight="bold" className={`transition-transform duration-200 ${open ? "rotate-180" : "rotate-90"}`} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-16 max-h-[calc(100vh-4rem)] overflow-y-auto bg-white dark:bg-neutral-900 text-zinc-900 dark:text-white shadow-2xl border-t border-zinc-200 dark:border-neutral-800 z-50 p-2 space-y-1 animate-in fade-in slide-in-from-top-4 duration-200">
          {allowedGroups.map(g => (
            <div key={g.title} className="bg-zinc-50 dark:bg-neutral-950/40 rounded-xl p-1.5 border border-zinc-100 dark:border-neutral-800/40">
              <div className="px-3 py-1.5 text-[10px] font-bold font-mono uppercase tracking-widest text-zinc-400 dark:text-neutral-500">{g.title}</div>
              <div className="space-y-0.5 mt-1">
                {g.items.map(item => (
                  <NavLink 
                    key={item.to} 
                    to={item.to}
                    className={({ isActive }) => `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      isActive 
                        ? "bg-[color:var(--kr-orange)]/10 text-[color:var(--kr-orange)] font-bold shadow-sm" 
                        : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-150/50 dark:hover:bg-neutral-800/50"
                    }`}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}